import { ColumnDef, flexRenderComponent } from '@tanstack/angular-table';
import { TableColorCellComponent } from '@core/components/table/table-color-cell/table-color-cell.component';
import { TableDateCellComponent } from '@core/components/table/table-date-cell/table-date-cell.component';
import { TableButtonCellComponent } from '@core/components/table/table-button-cell/table-button-cell.component';
import { capitalizeFirstLetter } from '@core/utils/capitalize-first-letter.util';
import { TablePercentCellComponent } from '@core/components/table/table-percent-cell/table-percent-cell.component';
import {
  STATUSES_ALLOW_REFUND,
  STATUSES_ERROR,
} from '../constants/statuses.constants';
import { JackpotHistory } from '../interfaces/jackpot-history.interface';
import { FairnessButtonComponent } from '@core/components/fairness-button/fairness-button.component';
import { ModalService } from '@core/modals/services/modal.service';
import { UserHistoryService } from '../services/user-history.service';
import { CostComponent } from '@core/components/cost/cost.component';

interface Props {
  userHistoryService: UserHistoryService;
  modalService: ModalService;
}

export const jackpotHistoryTableConfig = ({
  modalService,
  userHistoryService,
}: Props): ColumnDef<JackpotHistory>[] => [
  {
    accessorFn: (row) => row.gameid,
    id: 'gameid',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'ID',
      };
    },
    cell: (cell) => cell.getValue(),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.value,
    id: 'value',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Amount',
      };
    },
    cell: (cell) => {
      const color = cell.row.original.winner ? 'var(--green)' : 'var(--white)';
      return flexRenderComponent(CostComponent, {
        inputs: {
          cost: cell.getValue() as number,
          balanceType: 'skins',
          gap: 4,
          color: color,
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.userdata?.total_value,
    id: 'userdata.total_value',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Your Bet',
      };
    },
    cell: (cell) => {
      return flexRenderComponent(CostComponent, {
        inputs: {
          cost: cell.getValue() as number,
          balanceType: 'skins',
          gap: 4,
          color: cell.row.original.winner ? 'var(--green)' : 'var(--red)',
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.winner,
    id: 'details',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Details',
      };
    },
    cell: (cell) => {
      const { ...jackpotHistory } = cell.row.original;

      return jackpotHistory.winner
        ? flexRenderComponent(TableButtonCellComponent, {
            inputs: {
              text: 'View Items',
            },
            outputs: {
              handleClick: () => {
                modalService.openModal('JackpotSkinsModal', {
                  skins: userHistoryService.getSkinsByHistorySkins(
                    jackpotHistory.skins
                  ),
                  user: {
                    name: jackpotHistory.winnername,
                    steamid: jackpotHistory.winnerid,
                    avatar: jackpotHistory.winneravatar,
                    level: jackpotHistory.winnerlevel ?? 0,
                  },
                  color: jackpotHistory.winnercolor,
                  game: { gameid: jackpotHistory.gameid },
                });
              },
            },
          })
        : '-';
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.userdata.chances,
    id: 'userdata.chances',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Win Percentage',
      };
    },
    cell: (cell) => {
      return flexRenderComponent(TablePercentCellComponent, {
        inputs: {
          value: `${cell.getValue()}%`,
          disabledFormat: true,
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.winner,
    id: 'winner',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Result',
      };
    },
    cell: (cell) => {
      const isWon = cell.cell.getValue() as boolean;
      return flexRenderComponent(TableColorCellComponent, {
        inputs: {
          value: isWon ? 'Won' : 'Lost',
          color: isWon ? 'var(--green)' : 'var(--red)',
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.ended,
    id: 'ended',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Date',
      };
    },
    cell: (cell) => {
      return flexRenderComponent(TableDateCellComponent, {
        inputs: {
          value: cell.cell.getValue() as number,
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.status,
    id: 'status',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Status',
      };
    },
    cell: (cell) => {
      const status = cell.cell.getValue() as string;
      const jp = cell.row.original;

      const isResend = STATUSES_ALLOW_REFUND.includes(status);

      if (isResend) {
        return flexRenderComponent(TableButtonCellComponent, {
          inputs: {
            text: 'Resend',
          },
          outputs: {
            handleClick: () =>
              userHistoryService.resendTrade(jp.gameid, jp.potid, 'jackpot'),
          },
        });
      } else {
        const getStatusText = () => {
          if (!jp.winner) {
            return {
              text: 'No Winnings',
            };
          }

          if (
            status === 'accepted' ||
            status === 'active' ||
            status === 'send'
          ) {
            return {
              text: capitalizeFirstLetter(status),
              color: 'var(--green)',
            };
          }

          if (status === 'refunded') {
            return {
              text: 'Refunded',
              color: 'var(--yellow)',
            };
          }

          if (STATUSES_ERROR.includes(status)) {
            return {
              text: `Error: ${status}`,
              color: 'var(--red)',
            };
          }

          return {
            text: status,
          };
        };

        const { text, color } = getStatusText();

        return flexRenderComponent(TableColorCellComponent, {
          inputs: {
            value: text,
            color: color,
          },
        });
      }
    },
    enableSorting: true,
  },
  {
    id: 'fairness',
    header: () => 'Fairness',
    cell: (cell) =>
      flexRenderComponent(FairnessButtonComponent, {
        inputs: {
          href: `/provably-fair/jackpot/${cell.row.original.gameid}`,
        },
        outputs: {
          handleClick: () => {
            modalService.close();
          },
        },
      }),
    enableSorting: false,
  },
];
